import { useState, useEffect } from 'react';
import { Button, Card } from '@pavanhiro/common-ui';

const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL || 'https://zoauth-auth-engine-owht3p65vq-uc.a.run.app';

interface OAuthClient {
  client_id: string;
  client_name: string;
  redirect_uris: string[];
  grant_types: string[];
  scopes: string[];
  created_at?: string;
}

export default function ApplicationsPage() {
  const [clients, setClients] = useState<OAuthClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewClientForm, setShowNewClientForm] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      // Placeholder - backend endpoint needs implementation
      // const resp = await fetch(`${API_BASE_URL}/api/v1/oauth/clients`, {
      //   headers: {
      //     'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      //   },
      // });
      // const data = await resp.json();
      // setClients(data.clients || []);
      
      // Demo data for now
      setClients([
        {
          client_id: 'admin-portal',
          client_name: 'Admin Portal',
          redirect_uris: ['https://id.zoauth.com/callback'],
          grant_types: ['authorization_code', 'refresh_token'],
          scopes: ['openid', 'profile', 'email', 'admin'],
        },
        {
          client_id: 'zmemo-app',
          client_name: 'ZMemo Application',
          redirect_uris: ['http://localhost:3000/callback', 'https://zmemo.com/callback'],
          grant_types: ['authorization_code', 'refresh_token'],
          scopes: ['openid', 'profile', 'email'],
        },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
      <div style={{ marginBottom: '40px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
          OAuth Applications
        </h1>
        <p style={{ color: '#64748b', fontSize: '16px' }}>
          Manage OAuth 2.0 clients for your organization. These clients can authenticate users and access your APIs.
        </p>
      </div>

      {error && (
        <div style={{ 
          padding: '16px', 
          background: '#fee2e2', 
          border: '1px solid #fca5a5',
          borderRadius: '8px',
          marginBottom: '24px',
          color: '#991b1b',
        }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={() => setShowNewClientForm(true)}
          variant="primary"
        >
          + New Application
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
          Loading applications...
        </div>
      ) : clients.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>
              No applications yet
            </h3>
            <p style={{ color: '#64748b', marginBottom: '24px' }}>
              Create your first OAuth application to get started
            </p>
            <Button onClick={() => setShowNewClientForm(true)} variant="primary">
              Create Application
            </Button>
          </div>
        </Card>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          {clients.map((client) => (
            <Card key={client.client_id}>
              <div style={{ padding: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                  <div>
                    <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '4px', color: '#1e293b' }}>
                      {client.client_name}
                    </h3>
                    <p style={{ color: '#64748b', fontSize: '14px', fontFamily: 'monospace' }}>
                      {client.client_id}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button variant="outline" size="sm">
                      Edit
                    </Button>
                    <Button variant="text" size="sm" style={{ color: '#dc2626' }}>
                      Delete
                    </Button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Redirect URIs
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {client.redirect_uris.map((uri, idx) => (
                        <code key={idx} style={{ fontSize: '13px', color: '#334155', background: '#f1f5f9', padding: '4px 8px', borderRadius: '4px' }}>
                          {uri}
                        </code>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Grant Types
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {client.grant_types.map((type) => (
                        <span key={type} style={{ 
                          fontSize: '12px', 
                          padding: '4px 10px', 
                          background: '#dbeafe', 
                          color: '#1e40af', 
                          borderRadius: '12px',
                          fontWeight: '500',
                        }}>
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Scopes
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {client.scopes.map((scope) => (
                        <span key={scope} style={{ 
                          fontSize: '12px', 
                          padding: '4px 10px', 
                          background: '#dcfce7', 
                          color: '#15803d', 
                          borderRadius: '12px',
                          fontWeight: '500',
                        }}>
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
